import { FC, useState, useEffect, useRef } from 'react'
import { classNames } from 'primereact/utils'
import { Control, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { DropdownStyle } from '../../utils/styling-constants'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import {
  AttitudeState,
  CFDFile,
  DomainState,
  PtapLocation,
  RHRepo,
  RunConfigCase,
  SelectedSystem,
  SimulationState,
  TyreState,
  WingState
} from '@aero-platform/shared'
import { isF1TeamState, currentTeamState } from '../../utils/state-atoms'
import { useRecoilValue } from 'recoil'
import IconButton from '../buttons/IconButton'
import { DeleteIcon, UploadIcon } from '../Icons'
import { NASCAR28Teams } from '../../utils/constants'
import { useSimTemplateUpload } from '../../hooks/useSimTemplateUpload'
import { parseRHEncodingComponents, rhComponentMatchesValue } from '../../utils/rh-encoding'
import { selectRHRepoForAttitudeState } from '../../utils/attitudeStateDomain'
import { isPerCaseUpload } from '../../utils/simTemplateOverride'

const prefix = '|'

const attitudeStateFields = [
  {
    name: 'frh',
    label: 'FRH',
    namePrefix: 'FR'
  },
  {
    name: 'rrh',
    label: 'RRH',
    namePrefix: 'RR'
  },
  {
    name: 'steer',
    label: 'Steer',
    namePrefix: 'S'
  },
  {
    name: 'roll',
    label: 'Roll',
    namePrefix: 'R'
  },
  {
    name: 'yaw',
    label: 'Yaw',
    namePrefix: 'Y'
  },
  {
    name: 'radius',
    label: 'Rad'
  },
  {
    name: 'speed',
    label: 'Spd'
  },
  {
    name: 'exhaust',
    label: 'Exh'
  },
  {
    name: 'spec',
    label: 'SPEC',
    nascar28Only: true
  },
  {
    name: 'radBlocker',
    label: 'RAD_BLOCKER',
    nascar28Only: true
  }
]

type NamePortion = {
  frh: string
  rrh: string
  yaw: string
  steer: string
  roll: string
  domain: string
  simulation: string
  fw: string
  drs: string
}

export type CreateRunConfigCase = {
  selectedAttitudeStates: AttitudeState[]
  cases: RunConfigCase[]
  selectedTyreState?: TyreState
  selectedDomainState?: DomainState
  selectedSimulationState?: SimulationState
  selectedWingState?: WingState
  selectedPtapLocation?: PtapLocation
  selectedRHRepo?: RHRepo
  selectedSystems?: SelectedSystem[]
  useDeformableTires?: boolean
}

type CaseCreatorLineProps = {
  submitting: boolean
  control: Control<CreateRunConfigCase>
  setValue: (field: string, value: any) => void
  getValues: () => CreateRunConfigCase
  caseName: string
  caseIndex: number
  caseItem: RunConfigCase
  rhrepoStates: RHRepo[]
  onDelete: (caseIndex) => void
  /** Report whether this row's per-case .sim upload is still pending (uploading/verifying/failed). */
  onSimReadinessChange?: (caseName: string, pending: boolean) => void
}

const CaseCreatorLine: FC<CaseCreatorLineProps> = ({
  submitting,
  control,
  setValue,
  getValues,
  caseName,
  caseIndex,
  caseItem,
  rhrepoStates,
  onDelete,
  onSimReadinessChange
}) => {
  const [rhRepoStates, setRhRepoStates] = useState<RHRepo[]>(rhrepoStates || [])
  const [baseAttitudeState] = useState<AttitudeState>(
    caseItem.selectedAttitudeState || ({} as AttitudeState)
  )
  const [noMatchFound, setNoMatchFound] = useState<boolean>(false)

  // Update RH repo states when parent prop changes
  useEffect(() => {
    setRhRepoStates(rhrepoStates || [])
  }, [rhrepoStates])
  const [nameSegment, setNameSegment] = useState<NamePortion>({
    frh: '',
    rrh: '',
    yaw: '',
    steer: '',
    roll: '',
    fw: '',
    drs: '',
    domain: '',
    simulation: ''
  } as NamePortion)

  const isF1Team = useRecoilValue(isF1TeamState)
  const currentTeam = useRecoilValue(currentTeamState)
  const isNascar28 = NASCAR28Teams.includes(currentTeam)

  const visibleAttitudeStateFields = attitudeStateFields.filter(f => !f.nascar28Only || isNascar28)

  const handleDelete = lineNum => {
    onDelete && onDelete(lineNum)
  }

  // Per-case .sim upload (mirror of edit-case): direct-to-blob into a permanent sim-templates
  // folder. We await the transfer AND confirm the blob is present in Azure before attaching it
  // to the case, so a new run can never be submitted against a sim that never landed.
  const sim = useSimTemplateUpload()
  const simFileInputRef = useRef<HTMLInputElement>(null)
  const [perCaseSimFile, setPerCaseSimFile] = useState<string>(
    isPerCaseUpload(caseItem.simTemplate) ? caseItem.simTemplate.file : ''
  )

  // Report upload readiness up so the run can't be saved while this row is uploading/verifying or
  // after a failed verify (both would persist the previous/default SIM). On unmount we clear the
  // pending marker; useSimTemplateUpload separately aborts any in-flight transfer on unmount, so a
  // removed/reselected row can't later publish its SIM into the form.
  useEffect(() => {
    const pending = sim.phase === 'uploading' || sim.phase === 'verifying' || sim.phase === 'failed'
    onSimReadinessChange?.(caseName, pending)
    return () => onSimReadinessChange?.(caseName, false)
  }, [sim.phase, caseName, onSimReadinessChange])

  const handlePerCaseSimUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (simFileInputRef.current) simFileInputRef.current.value = ''
    if (!file) return
    const landedTemplate = await sim.startUpload(file, { runName: file.name })
    if (landedTemplate) {
      setValue(`cases.${caseIndex}.simTemplate`, landedTemplate as CFDFile)
      setPerCaseSimFile(file.name)
      toast.success('SIM file confirmed in Azure.')
    }
  }

  useEffect(() => {
    let newName = `${nameSegment.frh}${nameSegment.rrh}${nameSegment.yaw}${nameSegment.steer}${nameSegment.roll}${nameSegment.domain}${nameSegment.simulation}`
    if (nameSegment.fw || nameSegment.drs) {
      newName = `${newName}${prefix}${nameSegment.fw}_${nameSegment.drs}`
    }
    const finalName =
      baseAttitudeState && baseAttitudeState.name
        ? baseAttitudeState.name + newName
        : baseAttitudeState?.name || ''
    setValue(`cases.${caseIndex}.name`, finalName)
    // run when base attitude name or any name segment changes
  }, [
    baseAttitudeState?.name,
    nameSegment.frh,
    nameSegment.rrh,
    nameSegment.yaw,
    nameSegment.steer,
    nameSegment.roll,
    nameSegment.domain,
    nameSegment.simulation,
    nameSegment.fw,
    nameSegment.drs,
    caseIndex,
    setValue
  ])

  const handleAttitudeStateChange = (attitudes: AttitudeState) => {
    // Ensure we have valid numbers, default to 0 if undefined/null
    const frh = attitudes.frh || 0
    const rrh = attitudes.rrh || 0
    const roll = attitudes.roll || 0
    const yaw = attitudes.yaw || 0

    // Auto-populate ride height state if matching. Domain-duplicated states share
    // their source's encoding, so prefer this state's own repo / same domain.
    const matchedRHRepo = selectRHRepoForAttitudeState(rhRepoStates, attitudes, rhRepo => {
      const components = parseRHEncodingComponents(rhRepo.suspensionTypeEncoding)
      if (!components) return false
      return (
        rhComponentMatchesValue(components[0], frh) &&
        rhComponentMatchesValue(components[1], rrh) &&
        rhComponentMatchesValue(components[2], roll) &&
        rhComponentMatchesValue(components[3], yaw)
      )
    })

    if (!isF1Team && matchedRHRepo) {
      setValue(`cases.${caseIndex}.selectedRHRepo`, matchedRHRepo)
      setNoMatchFound(false) // Match found, clear no-match flag
    } else {
      // Set no-match flag only if we're not F1 team and actually searched for a match
      if (!isF1Team && !matchedRHRepo) {
        setNoMatchFound(true)
        // An RH file from a different RH set no longer applies to this case
        const currentRHRepo = getValues().cases?.[caseIndex]?.selectedRHRepo
        if (
          currentRHRepo &&
          !rhRepoStates.some(
            rhRepo =>
              rhRepo.path === currentRHRepo.path &&
              rhRepo.name === currentRHRepo.name &&
              rhRepo.suspensionTypeEncoding === currentRHRepo.suspensionTypeEncoding
          )
        ) {
          setValue(`cases.${caseIndex}.selectedRHRepo`, undefined)
        }
      }
    }
  }

  // Run attitude state matching on component mount and when RH repo states change
  useEffect(() => {
    if (baseAttitudeState && baseAttitudeState.name) {
      handleAttitudeStateChange(baseAttitudeState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseAttitudeState.name, rhRepoStates]) // Re-run if attitude state name or RH repo states change

  return (
    <div className="col-span-12 grid grid-cols-12 gap-x-2 gap-y-0 pb-2">
      <div className="field-box col-span-2">
        <Controller
          name={`cases.${caseIndex}.name`}
          control={control}
          disabled={submitting}
          render={({ field, fieldState }) => (
            <>
              {caseIndex === 0 && (
                <label htmlFor={field.name} className="mb-2">
                  Case Name
                </label>
              )}
              <div className="sr-only">{field.value}111</div>
              <InputText
                readOnly
                value={field.value}
                disabled={true}
                id={field.name}
                {...field}
                className={classNames({ error: fieldState.error })}
                pt={{
                  root: {
                    className:
                      'border-0 outline-none focus:outline-none !outline-transparent text-sm'
                  }
                }}
              />
            </>
          )}
        />
      </div>
      <div className="col-span-7 flex justify-end space-x-2">
        {visibleAttitudeStateFields.map(stateField => (
          <div
            className="field-box"
            key={`cases.${caseIndex}.selectedAttitudeState.${stateField.name}`}>
            <Controller
              name={`cases.${caseIndex}.selectedAttitudeState.${stateField.name}` as any}
              control={control}
              render={({ field, fieldState }) => (
                <>
                  {caseIndex === 0 && (
                    <label
                      htmlFor={`cases.${caseIndex}.selectedAttitudeState.${stateField.name}`}
                      className="mb-2">
                      {stateField.label}
                    </label>
                  )}
                  {[
                    'frh',
                    'rrh',
                    'steer',
                    'roll',
                    'yaw',
                    ...(isNascar28 ? [] : ['spec', 'radBlocker'])
                  ].includes(stateField.name) ? (
                    // Read-only display for attitude state fields
                    <InputText
                      id={`cases.${caseIndex}.selectedAttitudeState.${stateField.name}`}
                      readOnly
                      value={
                        field.value
                          ? ['spec', 'radBlocker'].includes(stateField.name)
                            ? field.value.toFixed(1)
                            : field.value.toFixed(3)
                          : '0.000'
                      }
                      className="w-full"
                      style={{ backgroundColor: '#f9fafb', color: '#6b7280' }}
                    />
                  ) : (
                    // Editable for other fields (radius, speed, exhaust) and spec/radBlocker for NASCAR-28
                    <InputNumber
                      inputId={`cases.${caseIndex}.selectedAttitudeState.${stateField.name}`}
                      readOnly={submitting}
                      mode="decimal"
                      value={field.value}
                      maxFractionDigits={['spec', 'radBlocker'].includes(stateField.name) ? 1 : 3}
                      minFractionDigits={['spec', 'radBlocker'].includes(stateField.name) ? 1 : 0}
                      inputClassName="w-full"
                      onChange={e => {
                        field.onChange(e.value)
                        const newNameSegment = {
                          ...nameSegment,
                          [stateField.name]:
                            (e.value || 0) !== 0
                              ? prefix + (e.value || 0).toFixed(0) + stateField.namePrefix
                              : ''
                        }
                        setNameSegment(newNameSegment)
                      }}
                    />
                  )}
                </>
              )}
            />
          </div>
        ))}
      </div>
      {/* Selected RH Repo */}
      <div className="field-box col-span-3">
        <Controller
          name={`cases.${caseIndex}.selectedRHRepo`}
          rules={{ required: false && (!isF1Team ? 'RH Repo state is required.' : false) }}
          control={control}
          render={({ field, fieldState }) => (
            <>
              {caseIndex === 0 && (
                <label htmlFor={field.name} className="mb-2">
                  Ride Height State
                </label>
              )}
              <div className="flex space-x-2 items-center">
                <Dropdown
                  value={field.value}
                  disabled={submitting}
                  title={
                    field.value
                      ? `${field.value.name}/${field.value.suspensionTypeEncoding}`
                      : noMatchFound
                        ? 'No matching RH file found — select manually'
                        : undefined
                  }
                  style={{ minWidth: 'calc(100% - 88px)' }}
                  pt={{
                    ...DropdownStyle,
                    root: {
                      ...DropdownStyle.root,
                      className: 'basis-0 grow-1' + classNames(DropdownStyle.root.className)
                    }
                  }}
                  className={classNames({ error: fieldState.error })}
                  options={rhRepoStates}
                  optionValue="name"
                  valueTemplate={option => {
                    if (option) {
                      const fullText = `${option.name}/${option.suspensionTypeEncoding}`
                      // Smart truncation: if text is too long, show start + "..." + important end part
                      const maxLength = 25 // Adjust based on field width
                      let displayText = fullText
                      if (fullText.length > maxLength) {
                        const encoding = option.suspensionTypeEncoding
                        const truncatedName =
                          option.name.length > 12
                            ? option.name.substring(0, 9) + '...'
                            : option.name
                        displayText = `${truncatedName}/${encoding}`
                        // If still too long, truncate encoding too
                        if (displayText.length > maxLength) {
                          displayText = `${truncatedName}/...${encoding.slice(-8)}`
                        }
                      }

                      return (
                        <div title={fullText}>
                          <span className="truncate" style={{ maxWidth: '180px' }}>
                            {displayText}
                          </span>
                        </div>
                      )
                    } else if (noMatchFound) {
                      return (
                        <div style={{ color: '#f59e0b', fontStyle: 'italic' }}>
                          No matching RH file found
                        </div>
                      )
                    } else {
                      return 'Select Ride Height State'
                    }
                  }}
                  itemTemplate={option => (
                    <span>
                      {option.name}/{option.suspensionTypeEncoding}
                    </span>
                  )}
                  useOptionAsValue
                  placeholder={
                    noMatchFound ? 'No matching RH file found' : 'Select Ride Height State'
                  }
                  emptyMessage="&nbsp;&nbsp;No Data"
                />
                <input
                  ref={simFileInputRef}
                  type="file"
                  accept=".sim"
                  onChange={handlePerCaseSimUpload}
                  className="hidden"
                />
                <IconButton
                  severity={sim.phase === 'failed' ? 'danger' : perCaseSimFile ? 'success' : 'info'}
                  additionalClassName={
                    sim.phase === 'failed'
                      ? ' !bg-red-600 !border-red-600'
                      : perCaseSimFile
                        ? ' !bg-green-600 !border-green-600'
                        : ' !bg-blue-600 !border-blue-600'
                  }
                  disabled={submitting || sim.isBusy}
                  loading={sim.isBusy}
                  title={
                    sim.phase === 'failed'
                      ? 'SIM upload not confirmed in Azure — click to re-upload'
                      : sim.phase === 'verifying'
                        ? 'Confirming SIM in Azure…'
                        : perCaseSimFile
                          ? `Per-case SIM (confirmed in Azure): ${perCaseSimFile}`
                          : 'Upload per-case SIM (.sim)'
                  }
                  icon={<UploadIcon className="w-4 h-4 fill-white inline cursor-pointer" />}
                  onClick={() => simFileInputRef.current?.click()}
                />
                <IconButton
                  severity="danger"
                  icon={<DeleteIcon className="w-4 h-4 fill-white inline cursor-pointer" />}
                  onClick={() => {
                    handleDelete(caseIndex)
                  }}
                />
              </div>
            </>
          )}
        />
      </div>
    </div>
  )
}

export default CaseCreatorLine
